import { useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TranslatorContext } from '@jutro/locale';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { useAuthentication, AUTH_ERRORS } from 'gw-digital-auth-react';
import AuthMessageComponent from '../AuthMessageComponent/AuthMessageComponent';
import { isEmpty, checkEmailValidity } from '../LoginUtil';
import appConfig from 'app-config';
import messages from './ForgotPasswordComponent.messages';
import metadata from './ForgotPasswordComponent.metadata.json5';
import styles from '../CommonLogin.module.scss';

function ForgotPasswordComponent(props) {
    const translator = useContext(TranslatorContext);
    const [email, updateEmail] = useState('');
    const [pageError, updatePageError] = useState();
    const [pageView, setPageView] = useState();
    const { forgotPassword, authHeader } = useAuthentication();
    const { onBackToSignIn } = props;
    const {applicationDetails} = appConfig;

    useEffect(() => {
        document.title = `Forgot Password - ${applicationDetails.applicationName}`; 
        _.each(document.querySelectorAll('input'), (element) => {
            element.required = false;
        }); 
    }, []);

    const handleForgotPassword = useCallback(
        (evt) => {
            if (evt) {
                evt.preventDefault();
            }

            if (isEmpty(email) || !checkEmailValidity(email)) {
                updatePageError(AUTH_ERRORS.fieldIncorrect);
                return Promise.resolve();
            }

            return forgotPassword(email, authHeader)
                .then(() => {
                    setPageView('success');
                })
                .catch((err) => {
                    switch (err.error) {
                        case AUTH_ERRORS.emailNotFound:
                            setPageView(AUTH_ERRORS.emailNotFound);
                            break;
                        default:
                            setPageView(AUTH_ERRORS.invalidAttempt);
                            break;
                    }
                });
        },
        [authHeader, email, forgotPassword]
    );

    const readValue = useCallback(() => email, [email]);
    const writeValue = useCallback((value) => updateEmail(value), []);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveValue: readValue,
        resolveCallbackMap: {
            handleBackToSignIn: onBackToSignIn
        },
        resolveComponentMap: {
            authmessagecomponent: AuthMessageComponent
        }
    };

    const overrides = {
        '@field': {
            onValueChange: writeValue,
            required: true,
            showRequired: true
        },
        errorMsg: {
            visible: !_.isUndefined(pageError),
            message: translator(messages[pageError])
        },
        emailFieldContainer: {
            visible: !pageView
        },
        emailForm: {
            onSubmit: handleForgotPassword
        },
        actionsWrapperSingleButton: {
            className: classNames(styles.actionsWrapperSingleButton, {
                [styles.coupleButtonStyle]: !_.isUndefined(onBackToSignIn),
                [styles.singleButtonStyle]: _.isUndefined(onBackToSignIn)
            })
        },
        sendButton: {
            trigger: handleForgotPassword
        },
        linkToSignIn: {
            visible: !_.isUndefined(onBackToSignIn)
        },
        invalidAttemptError: {
            visible: pageView === AUTH_ERRORS.invalidAttempt
        },
        invalidEmailError: {
            visible: pageView === AUTH_ERRORS.emailNotFound
        },
        successfulContainer: {
            visible: pageView === 'success'
        }
    };

    return renderContentFromMetadata(metadata.componentContent, overrides, resolvers);
}

ForgotPasswordComponent.propTypes = {
    onBackToSignIn: PropTypes.func
};

ForgotPasswordComponent.defaultProps = {
    onBackToSignIn: undefined
};

export default ForgotPasswordComponent;
